// Settings → Site settings → Alerts: which alert emails the picked site sends and to whom. Shown
// to admins and to owners (for the sites they own); the API refuses everyone else. The site comes
// from the Site settings picker (settings.ts).
import type { AlertIncidentView, AlertsView, AlertSettingsView } from "../src/alerts-types";

interface Opts {
  el: HTMLElement;
  /** The site whose alerts to show; the caller only passes one this person can manage. */
  site: { id: string; name: string };
  current: () => boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

type Flag = Exclude<keyof AlertSettingsView, "silent_hours">;
const RULES: { key: Flag; title: string; desc: string }[] = [
  { key: "silent_on", title: "Site stops sending data", desc: "No visits or events at all for the hours below, at a time the site normally has some. Usually means the tracking snippet broke." },
  { key: "drop_on", title: "Traffic drop", desc: "Visitors over the last 3 hours far below the usual level for that time of the week." },
  { key: "spike_on", title: "Traffic spike", desc: "Visitors over the last 3 hours far above the usual level." },
  { key: "goal_on", title: "Goal stopped converting", desc: "A goal that converts nearly every day had no conversions yesterday." },
  { key: "uptime_on", title: "Site down and certificate expiring", desc: "The site is checked every 5 minutes. Emails when it's down twice in a row, and 14 and 3 days before its security certificate expires." },
  { key: "error_spike_on", title: "Error spike", desc: "Far more errors than usual. Resolved and ignored errors don't count. MonoLog admins set the thresholds for every site." },
];

const KIND_LABEL: Record<AlertIncidentView["kind"], string> = {
  silent: "Stopped sending data",
  drop: "Traffic drop",
  spike: "Traffic spike",
  goal: "Goal stopped",
  down: "Site down",
  cert: "Certificate expiring",
  error_spike: "Error spike",
};

const ERRORS: Record<string, string> = {
  read_only: "Only owners of this site can change its alerts.",
  bad_silent_hours: "Pick one of the listed hours.",
};

const fmtWhen = (ms: number) => new Date(ms).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
function fmtSpan(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} days`;
}

function incident(i: AlertIncidentView): string {
  const what = i.kind === "goal" ? `${KIND_LABEL.goal}: ${esc(i.label ?? "")}` : i.kind === "cert" ? `${KIND_LABEL.cert} (${esc(i.label ?? "")} days)` : KIND_LABEL[i.kind];
  const span = i.closed_at ? `${fmtWhen(i.opened_at)} · lasted ${fmtSpan(i.closed_at - i.opened_at)}` : `since ${fmtWhen(i.opened_at)}`;
  return `<div class="alr-inc"><span class="pill ${i.closed_at ? "none" : "poor"}">${i.closed_at ? "Resolved" : "Ongoing"}</span><span class="alr-inc-what">${what}</span><span class="muted">${span}</span></div>`;
}

export async function renderAlerts(o: Opts): Promise<void> {
  const id = o.site.id;
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Alerts</h2></div><div class="acct-msg">Loading alerts…</div></div>`;
  let data: AlertsView;
  try {
    data = await call<AlertsView>("GET", `alerts?project=${encodeURIComponent(id)}`);
  } catch (err) {
    if (o.current()) o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Alerts</h2></div><div class="acct-msg error">Couldn't load alerts (${esc((err as Error).message)}).</div></div>`;
    return;
  }
  if (o.current()) draw(o, data);
}

function draw(o: Opts, a: AlertsView): void {
  const s = a.settings;
  const rules = RULES.map(
    (r) => `<label class="alr-rule"><input type="checkbox" name="${r.key}"${s[r.key] ? " checked" : ""}><span><b>${r.title}</b><span class="muted">${r.desc}</span>${
      r.key === "silent_on"
        ? `<span class="alr-hours">After <select class="btn" name="silent_hours" aria-label="Hours without data">${a.silent_hours_options
            .map((h) => `<option value="${h}"${h === s.silent_hours ? " selected" : ""}>${h} hours</option>`)
            .join("")}</select> without data</span>`
        : ""
    }</span></label>`,
  ).join("");
  const people = a.recipients.length
    ? a.recipients
        .map((r) => `<label class="alr-person"><input type="checkbox" name="to" value="${esc(r.email)}"${r.on ? " checked" : ""}><span class="usr-email">${esc(r.email)}</span><span class="muted">${r.role === "owner" ? "owner" : "admin"}</span></label>`)
        .join("")
    : `<div class="acct-msg">No one can get this site's alerts yet: add an owner in Users or Team.</div>`;
  const recent = a.incidents.length ? `<div class="alr-recent"><h3>Recent alerts</h3>${a.incidents.map(incident).join("")}</div>` : "";

  o.el.innerHTML = `<div class="card">
    <div class="card-head"><h2>Alerts</h2></div>
    <p class="muted usr-intro">MonoLog emails when something looks wrong with a site, and again when it's back to normal. Thresholds adapt to each site's usual traffic, so quiet sites don't get noisy alerts.</p>
    <form class="alr-form">
      <fieldset class="alr-rules"><legend>Send an email when</legend>${rules}</fieldset>
      <fieldset class="alr-people"><legend>Send to</legend>${people}<p class="muted usr-note">Owners of the site and MonoLog admins can get alerts. Viewers don't.</p></fieldset>
      <div class="usr-actions"><button class="btn primary" type="submit">Save alerts</button><span class="acct-msg" role="status"></span></div>
    </form>
    ${recent}
  </div>`;

  const form = o.el.querySelector<HTMLFormElement>(".alr-form")!;
  const hours = form.querySelector<HTMLSelectElement>('select[name="silent_hours"]')!;
  const silent = form.querySelector<HTMLInputElement>('input[name="silent_on"]')!;
  const sync = () => (hours.disabled = !silent.checked);
  silent.addEventListener("change", sync);
  sync();
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = form.querySelector<HTMLElement>("[role=status]")!;
    const f = new FormData(form);
    const settings: Partial<AlertSettingsView> = { silent_hours: Number(hours.value) };
    for (const r of RULES) settings[r.key] = f.get(r.key) === "on";
    const on = new Set(f.getAll("to").map(String));
    const recipients = a.recipients.map((r) => ({ email: r.email, on: on.has(r.email) }));
    try {
      const saved = await call<AlertsView>("PUT", "alerts", { project: a.project, settings, recipients });
      if (!o.current()) return;
      draw(o, saved);
      const m = o.el.querySelector<HTMLElement>("[role=status]")!;
      m.textContent = "Saved.";
      m.className = "acct-msg";
    } catch (err) {
      msg.textContent = ERRORS[(err as Error).message] ?? `Couldn't save (${(err as Error).message}).`;
      msg.className = "acct-msg error";
    }
  });
}
